import React, { useMemo, useState } from "react";
import spells from "../../data/primal-spells.json";

import "./PrimalSpellsClean.mobile.css";

const titleCase = (str) =>
  String(str)
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

const normalizeParts = (raw) =>
  String(raw || "")
    .replace(/[+/]/g, ",")
    .replace(/\b(and|or)\b/gi, ",")
    .replace(/\bbasic\s+/gi, "")
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p && p !== "-");

const deriveTags = (spell) => {
  const tags = new Set();

  // Actions: support hyphenated patterns like '◆-◆◆◆' or '◆◆-◆◆◆'
  const actions = String(spell.actions || "");
  if (actions.includes("⤶")) tags.add("Reaction");
  const parts = actions
    .split(/-/)
    .map((p) => (p.match(/◆/g) || []).length)
    .filter((n) => n > 0);
  if (parts.length === 1) {
    const c = Math.min(3, parts[0]);
    if (c > 0) tags.add(`${c}-Action`);
  } else if (parts.length > 1) {
    const min = Math.max(1, Math.min(...parts));
    const max = Math.min(3, Math.max(...parts));
    for (let i = min; i <= max; i++) tags.add(`${i}-Action`);
  }

  // Damage types
  normalizeParts(spell.damageType).forEach((d) => tags.add(titleCase(d)));

  // Defense (e.g., 'basic Reflex', 'AC', 'Fortitude')
  normalizeParts(spell.defense).forEach((d) => {
    const key = String(d || "").toLowerCase();
    if (key === "ac") tags.add("AC");
    else tags.add(titleCase(d));
  });

  // Targets: group into AOE / Self / Enemy(s) / Ally(s)
  const targetsRaw = String(spell.targets || "").trim();
  const nameRaw = String(spell.name || "")
    .toLowerCase()
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'");
  const descRaw = String(spell.description || "")
    .toLowerCase()
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'");
  const isHeal = /\bheal\b/.test(nameRaw) || /\bheal\b/.test(descRaw);

  // Manual categorization for specific spells
  const enemySpells = [
    "curse of lost time",
    "hydraulic push",
    "day's weight",
    "electric arc",
    "slashing gust",
    "splinter volley",
    "blazing bolt",
    "brine dragon bile",
    "coral scourge",
  ];
  // Expand enemy list - user's corrections
  enemySpells.push(
    "frostbite",
    "gouging claw",
    "ignition",
    "live wire",
    "needle darts",
    "puff of poison",
    "tangle vine",
    "vitality lash",
    "briny bolt",
    "camel spit",
    "charm",
    "fear",
    "goblin pox",
    "horizon thunder sphere",
    "leaden steps",
    "lose the path",
    "tether",
    "thunderstrike",
    "acid grip",
    "albatross curse",
    "fear the sun",
    "frog tongue",
    "heat metal",
    "holy light",
    "moonlight ray",
    "slow",
    "croak voice",
    "spout",
    "bestial curse",
    "bloodspray curse",
    "cinder swarm",
    "draw the lightning",
    "grasp of the deep",
    "murderous vine",
  );
  const allySpells = [
    "gentle landing",
    "life connection",
    "dancing shield",
    "cloud dragon's cloak",
    "water breathing",
    "loose time's arrow",
  ];
  // Expand ally list - user's corrections
  allySpells.push(
    "rousing splash",
    "guidance",
    "conductive weapon",
    "runic body",
    "runic weapon",
    "shattering gem",
    "blazing armory",
    "clear mind",
    "enlarge",
    "cleanse affliction",
    "gecko grip",
    "hidebound",
    "oaken resistance",
    "propulsive breeze",
    "resist energy",
    "restoration",
    "rubble step",
    "sound body",
    "sure footing",
    "thermal remedy",
    "haste",
    "organsight",
    "sand form",
  );
  const companionSpells = [
    "protect companion",
    "pet cache",
    "thundering dominance",
    "envenom companion",
    "familiar's face",
  ];
  const minionSpells = ["final sacrifice"];
  const aoeAdditions = ["heal", "horizon thunder sphere", "floating flame"];

  const isEnemySpell = enemySpells.some((s) => nameRaw.includes(s));
  const isAllySpell = allySpells.some((s) => nameRaw.includes(s));
  const isCompanionSpell = companionSpells.some((s) => nameRaw.includes(s));
  const isMinionSpell = minionSpells.some((s) => nameRaw.includes(s));
  // Removed isOtherSpell logic
  const hasAOEAddition = aoeAdditions.some((s) => nameRaw.includes(s));

  if (targetsRaw === "-" || targetsRaw === "") {
    // For targetless spells, mark them as Companion/Minion if applicable
    if (isCompanionSpell) {
      tags.add("Companion");
    } else if (isMinionSpell) {
      tags.add("Minion");
    }
  } else if (isCompanionSpell) {
    tags.add("Companion");
  } else if (isMinionSpell) {
    tags.add("Minion");
  } else if (isEnemySpell) {
    tags.add("Enemy(s)");
  } else if (isAllySpell) {
    tags.add("Ally(s)");
  } else {
    const lowerTargets = targetsRaw.toLowerCase();
    // Check for AOE patterns including contiguous squares/cubes
    if (
      /\b(cone|line|burst|emanation|contiguous\s+(squares|cubes)|two\s+cubes)\b/.test(
        lowerTargets,
      )
    ) {
      tags.add("AOE");
    } else if (/\bself\b/.test(lowerTargets)) {
      tags.add("Self");
    } else if (/\benemy|foe|opponent\b/.test(lowerTargets)) {
      tags.add("Enemy(s)");
    } else if (/\b(willing\s+)?creature|ally|allies\b/.test(lowerTargets)) {
      tags.add("Ally(s)");
    } else if (isEnemySpell) {
      tags.add("Enemy(s)");
    } else if (isAllySpell) {
      tags.add("Ally(s)");
    } else if (/\bcreature(s)?\b/.test(lowerTargets)) {
      // Ambiguous - skip Targeted tag entirely
    } else {
      // No tag for other targeted spells - user removed "Targeted" filter
    }
  }

  // Add companion/minion/other tags
  if (hasAOEAddition && !tags.has("AOE")) tags.add("AOE");

  // For heal spells, add both Ally(s) and Enemy(s) tags
  if (isHeal) {
    tags.add("Ally(s)");
    tags.add("Enemy(s)");
  }

  // Level tag
  if (spell.level) tags.add(titleCase(spell.level));

  // Extract all tags from spell.tags array if present
  if (spell.tags && Array.isArray(spell.tags)) {
    spell.tags.forEach((t) => {
      if (typeof t === "string" && t.trim()) {
        let norm = t.trim().toLowerCase();
        if (norm === "aoe") tags.add("AOE");
        else if (norm === "ac") tags.add("AC");
        else if (["enemy(s)", "enemies"].includes(norm)) tags.add("Enemy(s)");
        else if (["ally(s)", "allies"].includes(norm)) tags.add("Ally(s)");
        else if (["self"].includes(norm)) tags.add("Self");
        else if (["companion"].includes(norm)) tags.add("Companion");
        else if (["minion"].includes(norm)) tags.add("Minion");
        // Removed 'other' tag addition
        else tags.add(titleCase(t));
      }
    });
  }

  return Array.from(tags).filter((t) => t !== "Other" && Boolean(t));
};

const isMobile = () => window.innerWidth <= 600;

const PrimalSpells = () => {
  const [activeTags, setActiveTags] = useState([]);
  const [openGroup, setOpenGroup] = useState(isMobile() ? null : undefined);
  const [searchQuery, setSearchQuery] = useState("");

  // build grouped tag lists (Action, Rank, Target, Defense, Damage)
  const allTagsByGroup = useMemo(() => {
    const groups = {
      Action: new Set(),
      Rank: new Set(),
      Target: new Set(),
      Defense: new Set(),
      Damage: new Set(),
      Utility: new Set(),
      // Removed Other group
    };

    const damageKeywords = new Set([
      "fire",
      "cold",
      "cold iron",
      "electricity",
      "poison",
      "bludgeoning",
      "slashing",
      "piercing",
      "acid",
      "vitality",
      "sonic",
      "spirit",
      "void",
      "negative",
      "precision",
      "silver",
    ]);

    const defenseKeywords = new Set(["ac", "reflex", "fortitude", "will"]);

    spells.forEach((s) => {
      const levelTag = s.level ? titleCase(s.level) : null;
      const t = deriveTags(s);
      t.forEach((tag) => {
        // drop 4-Action/5-Action tags if present
        if (/^(4|5)-Action$/.test(tag)) return;
        const lower = String(tag).toLowerCase();
        if (/-Action$/.test(tag) || tag === "Reaction") groups.Action.add(tag);
        else if (levelTag && tag === levelTag) groups.Rank.add(tag);
        else if (
          [
            "aoe",
            "self",
            "enemy(s)",
            "ally(s)",
            "companion",
            "minion",
            // Removed 'other' from group list
          ].includes(lower)
        )
          groups.Target.add(tag);
        else if (defenseKeywords.has(lower)) groups.Defense.add(tag);
        else if (damageKeywords.has(lower)) groups.Damage.add(tag);
        else if (["buff", "debuff", "control"].includes(lower))
          groups.Utility.add(tag);
        // Removed Other group addition
      });
    });

    return Object.fromEntries(
      Object.entries(groups).map(([k, s]) => [k, Array.from(s).sort()]),
    );
  }, []);

  const toggleTag = (tag) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const filtered = useMemo(() => {
    let result = spells;
    if (activeTags.length) {
      result = result.filter((s) => {
        const t = deriveTags(s);
        return activeTags.every((a) => t.includes(a));
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((s) => {
        return (
          String(s.name || "")
            .toLowerCase()
            .includes(q) ||
          String(s.description || "")
            .toLowerCase()
            .includes(q) ||
          String(s.actions || "")
            .toLowerCase()
            .includes(q) ||
          String(s.damageType || "")
            .toLowerCase()
            .includes(q) ||
          String(s.defense || "")
            .toLowerCase()
            .includes(q) ||
          String(s.targets || "")
            .toLowerCase()
            .includes(q)
        );
      });
    }
    return result;
  }, [activeTags, searchQuery]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((s) => {
      (map[s.level] = map[s.level] || []).push(s);
    });
    // Sort all spells alphabetically by name within each level group
    Object.keys(map).forEach((level) => {
      map[level] = map[level].slice().sort((a, b) => {
        const nameA = String(a.name || "").toLowerCase();
        const nameB = String(b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    });
    return map;
  }, [filtered]);

  const rankTags = useMemo(() => {
    const allRanks = allTagsByGroup.Rank || [];
    return allRanks.sort((a, b) => {
      // Extract level numbers; "Cantrips Combat" comes first
      const aNum =
        a === "Cantrips Combat" ? -1 : parseInt(a.match(/\d+/)?.[0] || 0);
      const bNum =
        b === "Cantrips Combat" ? -1 : parseInt(b.match(/\d+/)?.[0] || 0);
      return aNum - bNum;
    });
  }, [allTagsByGroup.Rank]);

  return (
    <div className="primal-spells">
      <h1>Primal Spells</h1>
      <p>Click tags to filter spells. Multiple tags are combined (AND).</p>

      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search spells by name, description, etc."
          style={{
            width: "100%",
            maxWidth: 400,
            padding: "8px 12px",
            fontSize: "1em",
            borderRadius: 6,
            border: "1px solid #bbb",
            marginBottom: 10,
          }}
        />
      </div>

      <div
        style={{
          marginBottom: 20,
          padding: 16,
          backgroundColor: "#f9f9f9",
          borderRadius: 8,
          border: "1px solid #ddd",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: isMobile() ? "wrap" : "nowrap",
            gap: 20,
            overflowX: isMobile() ? "visible" : "auto",
            paddingBottom: 8,
          }}
        >
          {/* ...existing code... */}
          {[
            ["Action", allTagsByGroup.Action],
            ["Rank", rankTags],
            ["Target", allTagsByGroup.Target],
            ["Defense", allTagsByGroup.Defense],
            ["Damage", allTagsByGroup.Damage],
            ["Utility", allTagsByGroup.Utility],
            ["Other", allTagsByGroup.Other],
          ].map(([group, tags]) =>
            tags && tags.length > 0 ? (
              <div
                key={group}
                className={isMobile() ? "filter-group-mobile" : undefined}
              >
                {/* ...existing code... */}
                <strong
                  style={{
                    display: "block",
                    marginBottom: 10,
                    fontSize: "1.1em",
                    color: "#333",
                    cursor: isMobile() ? "pointer" : "default",
                    userSelect: "none",
                  }}
                  onClick={
                    isMobile()
                      ? () => setOpenGroup(openGroup === group ? null : group)
                      : undefined
                  }
                  aria-expanded={isMobile() ? openGroup === group : undefined}
                >
                  {group}
                  {isMobile() && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: "0.9em",
                        color: "#888",
                      }}
                    >
                      {openGroup === group ? "▲" : "▼"}
                    </span>
                  )}
                </strong>
                {(!isMobile() || openGroup === group) && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginBottom: isMobile() ? 6 : undefined,
                    }}
                  >
                    {/* ...existing code... */}
                    {group === "Rank" ? (
                      <>
                        {tags
                          .filter((tag) => tag === "Cantrips Combat")
                          .map((tag) => (
                            <button
                              key={`${group}-${tag}`}
                              onClick={() => toggleTag(tag)}
                              style={{
                                padding: "8px 12px",
                                background: activeTags.includes(tag)
                                  ? "#0066cc"
                                  : "#e8e8e8",
                                color: activeTags.includes(tag)
                                  ? "#fff"
                                  : "#333",
                                border: "1px solid #999",
                                borderRadius: 6,
                                cursor: "pointer",
                                fontWeight: activeTags.includes(tag)
                                  ? "600"
                                  : "400",
                                transition: "background 0.2s, color 0.2s",
                              }}
                            >
                              {tag}
                            </button>
                          ))}
                        {tags
                          .filter((tag) => tag !== "Cantrips Combat")
                          .map((tag) => (
                            <button
                              key={`${group}-${tag}`}
                              onClick={() => toggleTag(tag)}
                              style={{
                                padding: "8px 12px",
                                background: activeTags.includes(tag)
                                  ? "#0066cc"
                                  : "#e8e8e8",
                                color: activeTags.includes(tag)
                                  ? "#fff"
                                  : "#333",
                                border: "1px solid #999",
                                borderRadius: 6,
                                cursor: "pointer",
                                fontWeight: activeTags.includes(tag)
                                  ? "600"
                                  : "400",
                                transition: "background 0.2s, color 0.2s",
                              }}
                            >
                              {tag}
                            </button>
                          ))}
                      </>
                    ) : (
                      tags.map((tag) => (
                        <button
                          key={`${group}-${tag}`}
                          onClick={() => toggleTag(tag)}
                          style={{
                            padding: "8px 12px",
                            background: activeTags.includes(tag)
                              ? "#0066cc"
                              : "#e8e8e8",
                            color: activeTags.includes(tag) ? "#fff" : "#333",
                            border: "1px solid #999",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontWeight: activeTags.includes(tag)
                              ? "600"
                              : "400",
                            transition: "background 0.2s, color 0.2s",
                          }}
                        >
                          {tag}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : null,
          )}
        </div>
        {activeTags.length > 0 && (
          <button
            onClick={() => setActiveTags([])}
            style={{
              marginTop: 16,
              padding: "8px 16px",
              background: "#d9534f",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: "600",
              transition: "background 0.2s",
            }}
          >
            Clear All Filters
          </button>
        )}
      </div>

      {Object.keys(grouped).length === 0 && <p>No spells match filters.</p>}

      {Object.entries(grouped).map(([level, spells]) => (
        <div key={level} style={{ marginBottom: 18 }}>
          <h2>
            {level} ({spells.length})
          </h2>
          <ul>
            {spells.map((spell, idx) => (
              <li key={idx} style={{ marginBottom: 8 }}>
                <span style={{ marginRight: 8, color: "#d4af37" }}>
                  {spell.stars || ""}
                </span>
                {spell.url ? (
                  <a href={spell.url} target="_blank" rel="noopener noreferrer">
                    {spell.name}
                  </a>
                ) : (
                  <span>{spell.name}</span>
                )}
                <div style={{ fontSize: "0.85em", color: "#444" }}>
                  {spell.actions && <span>Actions: {spell.actions} | </span>}
                  {spell.range && <span>Range: {spell.range} | </span>}
                  {spell.targets && <span>Targets: {spell.targets} | </span>}
                  {spell.defense && <span>Defense: {spell.defense} | </span>}
                  {spell.damageType && <span>Damage: {spell.damageType}</span>}
                </div>
                {spell.description && (
                  <div style={{ color: "#666", marginTop: 6 }}>
                    {spell.description}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default PrimalSpells;
